import React, { useRef } from 'react'
import { Button } from '@/components/ui/button'
import { useUIVersion } from '@/utils/UIVersionContext'
import {
  ExcelExport,
  ExcelExportColumnProps as KendoExcelExportColumnProps,
} from '@progress/kendo-react-excel-export'
import { dateTimePattern, formatLocalDateUTC } from 'utils/date'
import { flattenKeys, unflattenKeys } from 'utils/flatten'
import { isString } from 'utils/is-string'

type ExportDataItem = Record<string, unknown>
type FlattenedExportDataItem = ReturnType<typeof flattenKeys>

interface TableProps<T extends object> {
  children: React.ReactNode
  data: T[]
  extraActions?: React.ReactNode
  columns?: ExcelExportColumnProps[]
  hasPermissions?: boolean
}

interface ExcelExportColumnProps extends KendoExcelExportColumnProps {
  fields?: string[]
  exportFn?({ dataItem }: { dataItem: ExportDataItem }): string
}

type MapColumns = {
  [key: string]: ExcelExportColumnProps
}

interface FormatDateByColumn<T> {
  previousValue: ExportDataItem
  currentValue: [string, T]
  column: ExcelExportColumnProps
  gridProps: FlattenedExportDataItem
}

type FormatDateByColumnProps = <T>(props: FormatDateByColumn<T>) => ExportDataItem

const formatByColumn: FormatDateByColumnProps = (props) => {
  const { previousValue, currentValue, column, gridProps } = props
  const [key, value] = currentValue

  if (key && gridProps && column?.exportFn) {
    const dataItem = unflattenKeys(gridProps) as ExportDataItem

    return { ...previousValue, [key]: column?.exportFn({ dataItem }) }
  }

  if (key && value && isString(value) && dateTimePattern.test(value)) {
    return { ...previousValue, [key]: formatLocalDateUTC(value, true) }
  }

  return { ...previousValue, [key]: value }
}

const convertColumnsToMap = (columns: ExcelExportColumnProps[]): MapColumns =>
  columns?.reduce((previousValue: MapColumns, currentValue) => {
    if (currentValue?.fields) {
      const field = createFieldKeyFromFields(currentValue)

      return {
        ...previousValue,
        [field]: {
          field,
          ...currentValue,
        },
      }
    }

    if (currentValue?.field && isString(currentValue?.field)) {
      return {
        ...previousValue,
        [currentValue.field]: currentValue,
      }
    }

    return previousValue
  }, {}) || {}

export const createFieldName = (fields: string[] = []): string => {
  return fields
    .filter((x) => !!x)
    .map((x) => x.trim())
    .map((x) => x.replaceAll('.', '-'))
    .join('_')
}

const createFieldKeyFromFields = (currentValue: ExcelExportColumnProps): string => {
  return createFieldName(currentValue?.fields)
}

const findColumnByFieldName = (columns: ExcelExportColumnProps[], fieldName: string) => {
  return (
    columns.find((exportColumn) => exportColumn.fields?.includes(fieldName)) ||
    columns.find((exportColumn) => exportColumn?.field && fieldName.includes(exportColumn.field))
  )
}

type PrepareExportDataFunctionType = <T extends object>(
  data: T[],
  columns?: ExcelExportColumnProps[]
) => T[] | ExportDataItem[]

const prepareExportData: PrepareExportDataFunctionType = (data, columns) => {
  if (!columns) {
    return data
  }

  const mapColumns = convertColumnsToMap(columns)
  const flattenData = data.map((x) => flattenKeys(x))

  return flattenData.map((x) => {
    return Object.entries(x).reduce<ExportDataItem>((previousValue, [key, value]) => {
      const column = mapColumns?.[key] || findColumnByFieldName(columns, key)

      if (column?.fields) {
        const field = createFieldKeyFromFields(column)
        const currentDateData = formatByColumn({
          previousValue,
          currentValue: [field, value],
          column,
          gridProps: x,
        })

        return { ...previousValue, ...currentDateData }
      } else {
        const currentDateData = formatByColumn({
          previousValue,
          currentValue: [key, value],
          column,
          gridProps: x,
        })

        return { ...previousValue, ...currentDateData }
      }
    }, {})
  })
}

const prepareExportColumns = (columns: ExcelExportColumnProps[] = []) => {
  const mapColumns = convertColumnsToMap(columns)
  return Object.entries(mapColumns).map(([key, value]) => {
    return { ...value, field: key }
  })
}

type TableWithExportProps = <T extends object>(
  p: TableProps<T>
) => React.ReactElement<TableProps<T>>

export const TableWithExport: TableWithExportProps = (props) => {
  const tableExcelRef = useRef<ExcelExport | null>(null)

  return (
    <div data-test='table'>
      <div className='flex gap-2 justify-end mb-2'>
        {props.extraActions}
        <TableExportButton
          data={props.data}
          hasPermissions={props.hasPermissions}
          exportRef={tableExcelRef}
        />
      </div>
      <ExcelExport
        data={prepareExportData(props.data)}
        ref={(exporter) => (tableExcelRef.current = exporter)}
      >
        {props.children}
      </ExcelExport>
    </div>
  )
}

interface TableExportButtonProps<T extends object> {
  data: T[]
  exportRef?: React.RefObject<ExcelExport | null>
  columns?: ExcelExportColumnProps[]
  hasPermissions?: boolean
}

type TableExportButtonComponentProps = <T extends object>(
  p: TableExportButtonProps<T>
) => React.ReactElement<TableExportButtonProps<T>>

const TableExportButton: TableExportButtonComponentProps = (props) => {
  const { isNewUI } = useUIVersion()
  const handleExportExcel = () => {
    const data = prepareExportData(props.data, props.columns)
    const columns = prepareExportColumns(props.columns)

    if (props?.exportRef?.current) {
      if (columns) {
        props.exportRef.current.save(data, columns)
      } else {
        props.exportRef.current.save(data)
      }
    }
  }

  return (
    <Button
      onClick={handleExportExcel}
      size='sm'
      className={isNewUI ? '' : 'bg-[#e92020]! text-white!'}
      data-test='export-to-excel-button'
      disabled={props.hasPermissions !== undefined ? !props.hasPermissions : false}
    >
      Export To Excel
    </Button>
  )
}

export default TableWithExport
